package link.erpango.quip.data.db.entities

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull
import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOne
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import link.erpango.quip.db.QuipDb
import link.erpango.quip.domain.entities.DSparePart
import linkerpangoquipdb.Spare_part
import kotlin.coroutines.CoroutineContext

class SparePartDAO(
    val db: QuipDb
) : ISparePartTable {
    override suspend fun selectSparePart(id: Long): Spare_part? {
        return db.sparePartQueries.select_by_id(id).awaitAsOneOrNull()
    }

    override suspend fun selectByServerId(serverId: Long): Spare_part? {
        return db.sparePartQueries.select_by_server_id(serverId).awaitAsOneOrNull()
    }

    override suspend fun selectAllSpareParts(): List<Spare_part> {
        return db.sparePartQueries.select_all().awaitAsList()
    }

    override suspend fun updateSparePart(
        id: Long,
        serverId: Long?,
        categoryId: String,
        sparePartId: String,
        brand: String,
        model: String,
        additionalInfo: String,
        durabilityDays: Long?,
        quantity: Double?,
        unit: String?,
        reference: String?,
        signature: String?
    ) {
        db.sparePartQueries.update(
            id = id,
            server_id = serverId,
            category_id = categoryId,
            spare_part_id = sparePartId,
            brand = brand,
            model = model,
            additional_info = additionalInfo,
            durability_days = durabilityDays,
            quantity = quantity,
            unit = unit,
            signature = signature,
            reference = reference
        )
    }

    override suspend fun insertSparePart(
        serverId: Long?,
        categoryId: String,
        sparePartId: String,
        brand: String,
        model: String,
        additionalInfo: String,
        durabilityDays: Long?,
        quantity: Double?,
        unit: String?,
        reference: String?,
        signature: String?
    ) {
        db.sparePartQueries.insertSparePart(
            server_id = serverId,
            category_id = categoryId,
            spare_part_id = sparePartId,
            brand = brand,
            model = model,
            additional_info = additionalInfo,
            quantity = quantity,
            unit = unit,
            durability_days = durabilityDays,
            signature = signature,
            reference = reference
        )
    }

    override suspend fun lastInsertRowId(): Long {
        return db.sparePartQueries.last_insert_rowid().awaitAsOne()
    }

    override suspend fun insertMaintenanceSparePartRelation(maintenanceId: Long, sparePartId: Long) {
        db.maintenanceSparePartsQueries.insert(maintenanceId, sparePartId)
    }

    override fun observeSpareParts(coroutineContext: CoroutineContext): Flow<List<Spare_part>> {
        return db.sparePartQueries.select_all()
            .asFlow()
            .mapToList(coroutineContext)
    }

    override suspend fun selectSparePartsByMaintenance(maintenanceId: Long): List<DSparePart> {
        val maintenanceSpareParts =
            db.maintenanceSparePartsQueries
                .select_by_maintenance_id(maintenanceId)
                .awaitAsList()
        val result = ArrayList<DSparePart>(maintenanceSpareParts.size)
        for (partRelation in maintenanceSpareParts) {
            val part = db.sparePartQueries.select_by_id(partRelation.spare_part_id).awaitAsOne().toDSparePart()
            result.add(part)
        }
        return result
    }

    override suspend fun selectLatestSparePartByCategoryAndType(
        vehicleVin: String,
        categoryId: String,
        sparePartId: String
    ): DSparePart? {
        return db.sparePartQueries.select_latest_by_category_and_type_for_vehicle(
            vehicleVin,
            categoryId,
            sparePartId
        ).awaitAsOneOrNull()?.toDSparePart()
    }

    override suspend fun deleteSparePartById(id: Long) {
        db.sparePartQueries.delete_by_id(id)
    }

    override suspend fun deleteMaintenanceSparePartRelation(maintenanceId: Long) {
        db.maintenanceSparePartsQueries.delete_by_maintenance_id(maintenanceId)
    }

    override suspend fun selectDistinctBrands(): List<String> {
        return db.sparePartQueries.select_distinct_brands().awaitAsList()
    }

    override suspend fun selectDistinctModels(): List<String> {
        return db.sparePartQueries.select_distinct_models().awaitAsList()
    }

    override suspend fun selectDistinctDurabilityDays(): List<Int> {
        return db.sparePartQueries.select_distinct_durability_days().awaitAsList().map { it.toInt() }
    }

    override fun observeDistinctBrands(
        vehicleType: String?,
        coroutineContext: CoroutineContext
    ): Flow<List<String>> {
        return if (vehicleType != null) {
            db.sparePartQueries.select_distinct_brands_by_type(vehicleType)
                .asFlow()
                .mapToList(coroutineContext)
        } else {
            db.sparePartQueries.select_distinct_brands()
                .asFlow()
                .mapToList(coroutineContext)
        }
    }

    override fun observeDistinctModels(
        vehicleType: String?,
        coroutineContext: CoroutineContext
    ): Flow<List<String>> {
        return if (vehicleType != null) {
            db.sparePartQueries.select_distinct_models_by_type(vehicleType)
                .asFlow()
                .mapToList(coroutineContext)
        } else {
            db.sparePartQueries.select_distinct_models()
                .asFlow()
                .mapToList(coroutineContext)
        }
    }

    override fun observeDistinctDurabilityDays(coroutineContext: CoroutineContext): Flow<List<Int>> {
        return db.sparePartQueries.select_distinct_durability_days()
            .asFlow()
            .mapToList(coroutineContext)
            .map { list -> list.map { it.toInt() } }
    }
}

fun Spare_part.toDSparePart(): DSparePart {
    return DSparePart(
        id = server_id,
        localId = id,
        categoryId = category_id,
        sparePartId = spare_part_id,
        brand = brand,
        model = model,
        additionalInfo = additional_info,
        durabilityDays = durability_days?.toInt(),
        quantity = quantity ?: 1.0,
        unit = unit,
        signature = signature,
        reference = reference
    )
}